import { useCallback, useEffect, useState } from "react";
import { Outlet, useOutletContext, useParams, Navigate, useNavigate } from "react-router-dom";
import { ConnectButton, useCurrentAccount } from "@mysten/dapp-kit";
import { SuiAuthProvider, useSuiAuth } from "@/components/providers/SuiAuthProvider";
import { SuiAppShell } from "@/components/SuiAppShell";
import { SuiDashboardPage } from "@/pages/SuiDashboard";
import { SuiAgentsPage } from "@/pages/SuiAgents";
import { SuiCreateAgentPage } from "@/pages/SuiCreateAgent";
import { SuiAgentView } from "@/components/SuiAgentView";
import { SuiChroniclePage } from "@/pages/SuiChronicle";
import { SuiSettingsPage } from "@/pages/SuiSettings";
import { SuiAccountMenu } from "@/components/SuiAccountMenu";
import { api, type AgentObject } from "@/lib/api";
import { isSuiAgent, mergeStoredAgents, suiExplorer } from "@/lib/sui";
import { listStoredAgents } from "@/lib/suiConfig";
import { useProvisionAgent, type ProvisionStatus } from "@/lib/useProvisionAgent";
import { PREVIEW, PREVIEW_AGENTS } from "@/lib/suiPreview";
import type { Policy } from "@/components/SuiPolicyEditor";

type SuiCtx = { agents: AgentObject[]; token: string; refetch: () => Promise<void> };
function useSuiCtx() {
  return useOutletContext<SuiCtx>();
}

/** Connect a Sui wallet, then sign the challenge → backend session. */
function SuiGate() {
  const account = useCurrentAccount();
  const { status, signInSui, error } = useSuiAuth();
  useEffect(() => {
    if (account && status === "idle") void signInSui();
  }, [account, status, signInSui]);
  return (
    <div className="sg">
      <div className="sg__card">
        <div className="sg__kick">altheia · sui control plane</div>
        <h1 className="sg__title">Connect a Sui wallet to continue</h1>
        <p className="sg__sub">
          Your wallet is your operator identity. You sign a one-time challenge, no transaction, no spend.
        </p>
        <div className="sg__cta"><ConnectButton connectText="Connect Sui wallet" /></div>
        {status === "signing" && <div className="sg__note">Awaiting signature…</div>}
        {status === "error" && <div className="sg__err">{error}</div>}
        {account && status === "idle" && (
          <button className="sg__retry" onClick={() => void signInSui()}>Sign in</button>
        )}
      </div>
      <style>{`
        .sg { min-height: 100dvh; display: flex; align-items: center; justify-content: center; background: var(--bg); padding: var(--s-6); }
        .sg__card { max-width: 460px; text-align: center; }
        .sg__kick { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--accent); margin-bottom: var(--s-4); }
        .sg__title { font: var(--type-display-page); font-style: italic; color: var(--bone); margin: 0 0 var(--s-3); }
        .sg__sub { font: var(--type-body); color: var(--slate); max-width: 40ch; margin: 0 auto var(--s-5); line-height: 1.6; }
        .sg__cta { display: flex; justify-content: center; }
        .sg__note { font: var(--type-mono-address); color: var(--slate-2); margin-top: var(--s-4); }
        .sg__err { font: var(--type-mono-address); color: var(--oxblood); margin-top: var(--s-4); }
        .sg__retry { margin-top: var(--s-4); font: var(--type-label); letter-spacing: var(--tracking-label); text-transform: uppercase; color: var(--bone); border-bottom: 1px solid var(--accent); padding-bottom: 2px; }
      `}</style>
    </div>
  );
}

/** Authed shell: fetch the operator's Sui agents, render the nav + routed page. */
function SuiShellLayout() {
  const { token } = useSuiAuth();
  const [agents, setAgents] = useState<AgentObject[]>([]);
  const refetch = useCallback(async () => {
    if (!token) return;
    let backend: AgentObject[] = [];
    try {
      const r = await api.listAgents(token);
      backend = r.agents.filter((a) => isSuiAgent(a));
    } catch { /* backend down — still surface locally-minted agents below */ }
    setAgents(mergeStoredAgents(backend, listStoredAgents()));
  }, [token]);
  useEffect(() => { refetch(); }, [refetch]);

  const navAgents = agents.map((a) => ({ id: a.id, name: a.name, status: a.status }));
  const ctx: SuiCtx = { agents, token: token ?? "", refetch };
  return (
    <SuiAppShell agents={navAgents} account={<SuiAccountMenu />}>
      <Outlet context={ctx} />
    </SuiAppShell>
  );
}

function SuiAppInner() {
  const { status } = useSuiAuth();
  if (status === "hydrating") {
    return (
      <div style={{ minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)", color: "var(--slate-2)", font: "var(--type-mono-address)", letterSpacing: "0.18em", textTransform: "uppercase" }}>
        Restoring session…
      </div>
    );
  }
  if (status !== "ready") return <SuiGate />;
  return <SuiShellLayout />;
}

export function SuiApp() {
  return (
    <SuiAuthProvider>
      {PREVIEW ? <SuiPreviewLayout /> : <SuiAppInner />}
    </SuiAuthProvider>
  );
}

/** Gateless dummy shell for screenshots/QA (VITE_SUI_PREVIEW=1). Inert in prod. */
function SuiPreviewLayout() {
  const navAgents = PREVIEW_AGENTS.map((a) => ({ id: a.id, name: a.name, status: a.status }));
  const ctx: SuiCtx = { agents: PREVIEW_AGENTS, token: "preview", refetch: async () => {} };
  const acct = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 34, padding: "0 12px", border: "1px solid var(--line)", borderRadius: "var(--radius-pill)", background: "var(--bg-elev)", font: "var(--type-mono-address)", color: "var(--bone)" }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--accent)" }} /> 0x5740…fbc0
    </span>
  );
  return (
    <SuiAppShell agents={navAgents} account={acct}>
      <Outlet context={ctx} />
    </SuiAppShell>
  );
}

// Route containers (read the shell's Outlet context, pass as props).
export function SuiDashboardRoute() {
  return <SuiDashboardPage agents={useSuiCtx().agents} />;
}
export function SuiAgentsRoute() {
  return <SuiAgentsPage agents={useSuiCtx().agents} />;
}
export function SuiCreateRoute() {
  const { token, refetch } = useSuiCtx();
  const navigate = useNavigate();
  const { provision, status, error, reset, creds, clearCreds } = useProvisionAgent(token);
  async function onSubmit(policy: Policy) {
    const id = await provision(policy);
    if (id) await refetch();
    // Navigation is deferred: the one-time credentials modal must be acknowledged first.
  }
  return (
    <>
      <SuiCreateAgentPage onSubmit={onSubmit} />
      {status !== "idle" && status !== "done" && <ProvisionOverlay status={status} error={error} onDismiss={reset} />}
      {creds && <SuiCredentialsModal creds={creds} onDone={() => { const id = creds.agentId; clearCreds(); navigate(`/sui/agents/${id}`); }} />}
    </>
  );
}

/**
 * One-time agent-key reveal (D2): shown once at creation, never stored in the
 * browser or backend. The developer copies it to run their agent loop; lose it
 * and you re-mint. Acknowledge gate prevents skipping past an unsaved key.
 */
function SuiCredentialsModal({ creds, onDone }: { creds: { address: string; secret: string }; onDone: () => void }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [ack, setAck] = useState(false);
  const mask = "•".repeat(44);
  function copyKey() {
    void navigator.clipboard?.writeText(creds.secret).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }
  return (
    <div className="cm__scrim">
      <div className="cm" role="dialog" aria-modal="true">
        <div className="cm__kick">agent provisioned · save the key now</div>
        <h2 className="cm__title">This key is shown once</h2>
        <p className="cm__intro">Your agent signs its own transactions with this key. It runs in your process, never ours, and is bound by the on-chain policy. We do not store it, in your browser or our backend. Copy it now.</p>
        <div className="cm__row"><span className="cm__k">Agent address</span><a className="cm__v" href={suiExplorer.address(creds.address)} target="_blank" rel="noopener noreferrer">{creds.address.slice(0, 10)}…{creds.address.slice(-8)} ↗</a></div>
        <div className="cm__keybox">
          <code className="cm__key">{revealed ? creds.secret : mask}</code>
          <div className="cm__keybtns">
            <button type="button" onClick={() => setRevealed((r) => !r)}>{revealed ? "hide" : "reveal"}</button>
            <button type="button" onClick={copyKey}>{copied ? "copied ✓" : "copy"}</button>
          </div>
        </div>
        <label className="cm__ack"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> I've saved this key. It can't be shown again.</label>
        <button type="button" className="cm__done" disabled={!ack} onClick={onDone}>Continue to agent →</button>
        <style>{`
          .cm__scrim { position: fixed; inset: 0; z-index: 70; display: grid; place-items: center; padding: var(--s-5); background: color-mix(in srgb, var(--indigo) 32%, transparent); animation: cm-fade 160ms ease-out; }
          @keyframes cm-fade { from { opacity: 0; } to { opacity: 1; } }
          .cm { width: 460px; max-width: 100%; background: var(--bg-elev); border: 1px solid var(--line); border-radius: var(--r-md); box-shadow: 0 24px 60px -16px color-mix(in srgb, var(--indigo) 40%, transparent); padding: var(--s-6); transform-origin: center; animation: cm-in 200ms cubic-bezier(0.23,1,0.32,1); }
          @keyframes cm-in { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: none; } }
          .cm__kick { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--accent-deep); }
          .cm__title { font: var(--type-display-section); font-style: italic; margin: var(--s-2) 0 var(--s-3); }
          .cm__intro { font: var(--type-body-small); color: var(--slate); line-height: 1.55; margin: 0 0 var(--s-4); }
          .cm__row { display: flex; align-items: center; justify-content: space-between; gap: var(--s-3); padding: 10px 0; border-top: 1px solid var(--line-soft); font: var(--type-mono-data); }
          .cm__k { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate-3); }
          .cm__v { color: var(--bone); } .cm__v:hover { color: var(--accent-deep); }
          .cm__keybox { display: flex; align-items: center; gap: var(--s-3); margin: var(--s-3) 0; padding: var(--s-3) var(--s-4); border: 1px solid var(--oxblood); border-radius: var(--r-sm); background: var(--oxblood-tint); }
          .cm__key { flex: 1; font: var(--type-mono-data); color: var(--oxblood); overflow-x: auto; white-space: nowrap; }
          .cm__keybtns { display: flex; gap: 6px; flex: none; }
          .cm__keybtns button { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.06em; color: var(--slate-2); border: 1px solid var(--line); border-radius: var(--r-sm); padding: 5px 10px; transition: color 120ms ease, border-color 120ms ease, transform 120ms ease; }
          .cm__keybtns button:hover { color: var(--bone); border-color: var(--line-stamp); } .cm__keybtns button:active { transform: scale(0.95); }
          .cm__ack { display: flex; align-items: center; gap: 8px; font: var(--type-body-small); color: var(--bone-2); margin: var(--s-4) 0; cursor: pointer; }
          .cm__done { width: 100%; font: var(--type-label); text-transform: uppercase; letter-spacing: 0.04em; padding: 12px; border-radius: var(--r-sm); background: var(--indigo); color: var(--text-on-ink); transition: opacity 150ms ease, transform 120ms cubic-bezier(0.23,1,0.32,1); }
          .cm__done:disabled { opacity: 0.4; cursor: not-allowed; } .cm__done:active:not(:disabled) { transform: scale(0.99); }
        `}</style>
      </div>
    </div>
  );
}

const STEP_COPY: Record<Exclude<ProvisionStatus, "idle" | "done">, { title: string; sub: string }> = {
  funding: { title: "Funding the agent", sub: "Approve the gas top-up in your wallet." },
  provisioning: { title: "Minting the policy on-chain", sub: "Approve the wallet prompt — vault, deposit, policy, capability, and agent gas all settle in one signature." },
  recording: { title: "Recording the agent", sub: "Writing it to the fleet and the audit index." },
  error: { title: "Mint interrupted", sub: "A step was rejected or failed. Nothing is live until all steps complete." },
};

function ProvisionOverlay({ status, error, onDismiss }: { status: Exclude<ProvisionStatus, "idle" | "done">; error: string | null; onDismiss: () => void }) {
  const copy = STEP_COPY[status];
  const isError = status === "error";
  return (
    <div className="pov" role="dialog" aria-modal="true">
      <div className="pov__card">
        {!isError && <div className="pov__spin" aria-hidden="true" />}
        <h2 className="pov__title">{copy.title}</h2>
        <p className="pov__sub">{copy.sub}</p>
        {isError && error && <pre className="pov__err">{error}</pre>}
        {isError && <button type="button" className="pov__btn" onClick={onDismiss}>Dismiss</button>}
      </div>
      <style>{`
        .pov { position: fixed; inset: 0; z-index: 60; display: flex; align-items: center; justify-content: center; background: color-mix(in srgb, var(--bone) 28%, transparent); backdrop-filter: blur(3px); animation: pov-in 160ms ease-out; }
        @keyframes pov-in { from { opacity: 0; } to { opacity: 1; } }
        .pov__card { width: 420px; max-width: 90vw; padding: var(--s-7); background: var(--bg-elev); border: 1px solid var(--line); border-radius: var(--r-md); text-align: center; box-shadow: 0 24px 60px -20px rgba(27,16,96,0.35); }
        .pov__spin { width: 34px; height: 34px; margin: 0 auto var(--s-5); border: 2px solid var(--line); border-top-color: var(--indigo); border-radius: 50%; animation: pov-spin 700ms linear infinite; }
        @keyframes pov-spin { to { transform: rotate(360deg); } }
        .pov__title { font: var(--type-display-section); font-style: italic; font-size: 22px; margin: 0 0 var(--s-3); color: var(--bone); }
        .pov__sub { font: var(--type-body); color: var(--slate); line-height: 1.55; margin: 0; }
        .pov__err { margin: var(--s-4) 0 0; padding: var(--s-3); background: var(--oxblood-tint); border: 1px solid var(--oxblood); border-radius: var(--r-sm); font: var(--type-mono-address); color: var(--oxblood); white-space: pre-wrap; text-align: left; }
        .pov__btn { margin-top: var(--s-5); font: var(--type-label); letter-spacing: 0.04em; text-transform: uppercase; padding: 10px 20px; border: 1px solid var(--line); border-radius: var(--r-sm); color: var(--bone); transition: border-color 150ms ease; }
        .pov__btn:hover { border-color: var(--indigo); }
        @media (prefers-reduced-motion: reduce) { .pov__spin { animation-duration: 1600ms; } }
      `}</style>
    </div>
  );
}
export function SuiAgentDetailRoute() {
  const { agents, token } = useSuiCtx();
  const { id } = useParams();
  const agent = agents.find((a) => a.id === id);
  if (!agent) return <Navigate to="/sui/agents" replace />;
  return <SuiAgentView agent={agent} token={token} />;
}
export function SuiChronicleRoute() {
  const { agents, token } = useSuiCtx();
  return <SuiChroniclePage agents={agents} token={token} />;
}
export function SuiSettingsRoute() {
  return <SuiSettingsPage />;
}
